import type { BillingAccessMode } from '@/lib/billing'

export type BillingNotice =
  | { kind: 'allowance'; text: string }
  | { kind: 'paywall'; text: string }
  | { kind: 'complimentary'; text: string }

export type BillingPaywallAction = 'checkout' | 'portal'

type BillingNoticeStatus = {
  accessMode: BillingAccessMode
  effectivePlan?: 'free' | 'pro' | 'pro_plus'
  reportsUsed: number
  reportLimit: number | null
  reportsRemaining: number | null
}

export function getBillingNotice(status: BillingNoticeStatus | null): BillingNotice | null {
  if (!status) return null

  if (status.accessMode === 'payment_required') {
    return { kind: 'paywall', text: 'Tu periodo de acceso ha finalizado.' }
  }

  if (status.accessMode === 'complimentary') {
    return { kind: 'complimentary', text: 'Acceso de cortesía' }
  }

  if (status.accessMode === 'introductory') {
    if (status.reportLimit === null) return null
    return {
      kind: 'allowance',
      text: `${status.reportsUsed} de ${status.reportLimit} reportes gratuitos utilizados`,
    }
  }

  if (status.accessMode === 'grace') {
    if (status.reportLimit === null) return { kind: 'allowance', text: 'Acceso temporal activo' }
    return {
      kind: 'allowance',
      text: `${status.reportsUsed} de ${status.reportLimit} reportes utilizados`,
    }
  }

  if (status.accessMode === 'subscription' && status.effectivePlan === 'pro') {
    if (status.reportLimit === null) return { kind: 'allowance', text: 'Plan Pro activo' }
    return {
      kind: 'allowance',
      text: `${status.reportsUsed} de ${status.reportLimit} reportes utilizados`,
    }
  }

  return null
}

export function getBillingPaywallAction(input: {
  denialCode: string | null
  status: Pick<BillingNoticeStatus, 'accessMode' | 'effectivePlan'> | null
}): BillingPaywallAction {
  if (input.denialCode === 'REPORT_LIMIT_REACHED') return 'portal'
  if (input.status?.accessMode === 'subscription' || input.status?.accessMode === 'grace') return 'portal'
  return 'checkout'
}

// Etiqueta compartida: nunca presentar el plan legado como derecho vigente.
export function getBillingPlanLabel(status: Pick<BillingNoticeStatus, 'accessMode' | 'effectivePlan'> | null): string {
  if (!status) return 'Consultar plan'
  if (status.accessMode === 'complimentary') return 'Cortesía · ilimitado'
  if (status.accessMode === 'introductory') return 'Acceso gratuito'
  if (status.accessMode === 'payment_required') return 'Revisar suscripción'
  if (status.accessMode === 'grace') return 'Acceso temporal'
  return status.effectivePlan === 'pro_plus' ? 'Pro Plus' : status.effectivePlan === 'pro' ? 'Pro' : 'Acceso gratuito'
}
